import { Avatar as MuiAvatar } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import cx from 'classnames';

import getUserColor from '../../services/getUserColor';
import { palette } from '../../../theme';

export const DEFAULT_AVATAR_URL =
  'https://openclassrooms.com/bundles/common/images/avatar_defaut.png';

const SIZE_TINY = 'tiny';
const SIZE_SMALL = 'small';
const SIZE_MEDIUM = 'medium';
const SIZE_LARGE = 'large';
const SIZE_XLARGE = 'xlarge';

/* istanbul ignore next */
const styles = () => ({
  root: {
    fontSize: 14,
    textTransform: 'uppercase',
    color: palette.text.onContrasted,
    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
  },
  fallback: {
    color: palette.text.secondary,
    boxShadow: 'none',
    border: 'none',
  },
  avatarSize: {
    '&-tiny': {
      width: 32,
      height: 32,
    },
    '&-small': {
      width: 40,
      height: 40,
    },
    '&-medium': {
      width: 45,
      height: 45,
    },
    '&-large': {
      width: 60,
      height: 60,
    },
    '&-xlarge': {
      width: 104,
      height: 104,
    },
  },
});

const hasUserAvatar = (avatarUrl) =>
  !!avatarUrl && (!avatarUrl.match(/avatar_defaut\.png/) || avatarUrl.match(/gravatar\.com/));

const getInitials = (userName) => {
  if (!userName) {
    return null;
  }

  const nameParts = userName.split(' ');
  const firstPart = nameParts[0];
  const lastPart = nameParts.length >= 2 ? nameParts[nameParts.length - 1] : null;
  let initials = firstPart.charAt(0);

  if (lastPart) {
    initials += lastPart.charAt(0);
  }

  return initials;
};

const Avatar = ({
  classes = {},
  size = SIZE_SMALL,
  className = '',
  component: CustomComponent = 'span',
  userName = '',
  userId = 0,
  avatarUrl = '',
  ...otherProps
}) => {
  const initials = getInitials(userName);
  const showInitials = initials && !hasUserAvatar(avatarUrl);
  const background = hasUserAvatar(avatarUrl)
    ? { src: avatarUrl }
    : {
        style: {
          backgroundColor: initials ? getUserColor(userId, userName) : palette.skeleton.image,
        },
      };

  return (
    <MuiAvatar
      alt={userName || ''}
      {...otherProps}
      className={cx([classes.root, `${classes.avatarSize}-${size}`, className], {
        [classes.fallback]: !initials,
      })}
      component={CustomComponent}
      data-testid="avatar-root"
      {...background}
    >
      {showInitials ? initials : null}
    </MuiAvatar>
  );
};

Avatar.SIZE_TINY = SIZE_TINY;
Avatar.SIZE_SMALL = SIZE_SMALL;
Avatar.SIZE_MEDIUM = SIZE_MEDIUM;
Avatar.SIZE_LARGE = SIZE_LARGE;
Avatar.SIZE_XLARGE = SIZE_XLARGE;

/**
 * @deprecated
 * ### Description
 * `Avatar` has been deprecated and replaced by Classify `Avatar`.
 *
 * ### Example
 * ```
 * import Avatar from '@classify/react-core-components/Avatar';
 *
 * <Avatar size="xs" userName="Rosa Bonheur" userId="RV2508185" />
 * ```
 */
export default withStyles(styles)(Avatar);
